/**
 * Ρυθμίσεις σύνδεσης με το NestJS API — από μεταβλητές περιβάλλοντος (REACT_APP_*).
 */

function trimTrailingSlashes(url: string): string {
  return url.replace(/\/+$/, '')
}

/**
 * Βάση URL του API, χωρίς καταληκτικό /.
 * Προτεραιότητα: REACT_APP_API_URL → (αν REACT_APP_API_RELATIVE=true) κενό → PROTOCOL+HOST+PORT.
 */
export function getApiBaseUrl(): string {
  const explicit = process.env.REACT_APP_API_URL?.trim()
  if (explicit) return trimTrailingSlashes(explicit)

  if (process.env.REACT_APP_API_RELATIVE === 'true') {
    return ''
  }

  const protocol = process.env.REACT_APP_API_PROTOCOL ?? 'http'
  const host =
    process.env.REACT_APP_HOST ??
    process.env.REACT_APP_API_HOST ??
    'localhost'
  const port =
    process.env.REACT_APP_BACKEND_PORT ??
    process.env.REACT_APP_API_PORT ??
    '5000'

  if (protocol === 'http' && port === '80') {
    return `${protocol}://${host}`
  }
  if (protocol === 'https' && port === '443') {
    return `${protocol}://${host}`
  }

  return `${protocol}://${host}:${port}`
}

/** Κλειδί localStorage για JWT (κοινό με AuthContext). */
export const AUTH_TOKEN_STORAGE_KEY = 'proffer_access_token'

/** JWT μόνο για τον πίνακα διαχείρισης (ξεχωριστό από χρήστη εφαρμογής). */
export const ADMIN_TOKEN_STORAGE_KEY = 'proffer_admin_access_token'

let sessionRevokeNavigationScheduled = false
let adminSessionRevokeNavigationScheduled = false

/**
 * P4-007 — 401 σε προστατευμένο αίτημα: αποσύνδεση και μετάβαση στη σύνδεση.
 * Τα δημόσια login/signup endpoints χρησιμοποιούν apiPostPublic, ώστε το 401 τους να μείνει στη φόρμα.
 */
export function notifySessionRevokedIfNeeded(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY)
    window.dispatchEvent(new Event('proffer:auth-lost'))
    if (sessionRevokeNavigationScheduled) return
    const p = window.location.pathname
    if (p.startsWith('/login') || p.startsWith('/register')) return
    sessionRevokeNavigationScheduled = true
    window.location.assign(`${window.location.origin}/login?reason=session`)
  } catch {
    /* ignore */
  }
}

export function notifyAdminSessionRevokedIfNeeded(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY)
    window.dispatchEvent(new Event('proffer:admin-auth-lost'))
    if (adminSessionRevokeNavigationScheduled) return
    const p = window.location.pathname
    if (p.startsWith('/admin/login')) return
    adminSessionRevokeNavigationScheduled = true
    window.location.assign(`${window.location.origin}/admin/login?reason=session`)
  } catch {
    /* ignore */
  }
}

/** Headers με Authorization αν υπάρχει αποθηκευμένο token. */
export function getAuthHeaders(
  jsonBody?: boolean,
): Record<string, string> {
  const h: Record<string, string> = {}
  if (jsonBody) {
    h['Content-Type'] = 'application/json'
  }
  try {
    const t = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
    if (t) {
      h['Authorization'] = `Bearer ${t}`
    }
  } catch {
    /* ignore */
  }
  return h
}

export function getAdminAuthHeaders(
  jsonBody?: boolean,
): Record<string, string> {
  const h: Record<string, string> = {}
  if (jsonBody) {
    h['Content-Type'] = 'application/json'
  }
  try {
    const t = localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY)
    if (t) {
      h['Authorization'] = `Bearer ${t}`
    }
  } catch {
    /* ignore */
  }
  return h
}

/** Πλήρες URL για endpoint (π.χ. `/offers`) — σωστό και με κενό base (proxy). */
export function apiUrl(path: string): string {
  const base = getApiBaseUrl().replace(/\/$/, '')
  const p = path.startsWith('/') ? path : `/${path}`
  if (!base) return p
  return `${base}${p}`
}

/** Πλήρες URL για στατικά του API (π.χ. /uploads/logos/...) — χρήσιμο για PDF & preview. */
export function resolveBackendFileUrl(
  relativePath: string | null | undefined,
): string | null {
  if (!relativePath?.trim()) return null
  const p = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
  const base = getApiBaseUrl().replace(/\/$/, '')
  if (base) return `${base}${p}`
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${p}`
  }
  return p
}

export function formatApiMessage(json: { message?: unknown }): string {
  const m = json.message
  if (Array.isArray(m)) return m.join(' ')
  if (typeof m === 'string') return m
  return 'Το αίτημα απέτυχε.'
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(apiUrl(path), { headers: getAuthHeaders() })
  const ct = res.headers.get('content-type') ?? ''
  if (res.ok && ct.includes('text/html')) {
    throw new Error(
      'Λάθος διαδρομή API (λήφθηκε HTML). Στο dev, πρόσθεσε το prefix στο frontend/src/setupProxy.js και επανεκκίνησε το npm start.',
    )
  }
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifySessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

/** POST χωρίς Authorization (π.χ. σύνδεση διαχειριστή). */
export async function apiPostPublic<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifySessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

/** POST multipart (χωρίς Content-Type — το browser βάζει boundary). */
export async function apiPostMultipart<T>(path: string, body: FormData): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: getAuthHeaders(false),
    body,
  })
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifySessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'PATCH',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifySessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'PUT',
    headers: getAuthHeaders(true),
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifySessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

export async function apiDelete(path: string): Promise<void> {
  const res = await fetch(apiUrl(path), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  })
  if (res.status === 204 || res.status === 200) return
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifySessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
}

export async function apiGetAdmin<T>(path: string): Promise<T> {
  const res = await fetch(apiUrl(path), { headers: getAdminAuthHeaders() })
  const ct = res.headers.get('content-type') ?? ''
  if (res.ok && ct.includes('text/html')) {
    throw new Error(
      'Λάθος διαδρομή admin API (λήφθηκε HTML). Στο dev, έλεγξε το frontend/src/setupProxy.js και επανεκκίνησε το npm start.',
    )
  }
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifyAdminSessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

export async function apiPostAdmin<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: getAdminAuthHeaders(true),
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifyAdminSessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

export async function apiPutAdmin<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'PUT',
    headers: getAdminAuthHeaders(true),
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifyAdminSessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

export async function apiPatchAdmin<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'PATCH',
    headers: getAdminAuthHeaders(true),
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifyAdminSessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
  return json as T
}

export async function apiDeleteAdmin(path: string): Promise<void> {
  const res = await fetch(apiUrl(path), {
    method: 'DELETE',
    headers: getAdminAuthHeaders(),
  })
  if (res.status === 204 || res.status === 200) return
  const json = (await res.json().catch(() => ({}))) as { message?: unknown }
  if (!res.ok) {
    if (res.status === 401) {
      notifyAdminSessionRevokedIfNeeded()
    }
    throw new Error(formatApiMessage(json))
  }
}
